import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Session-scoped token storage (clears automatically when tab/browser closes) ──
const getStoredToken = () => sessionStorage.getItem('gaurilabs_access_token');
const setStoredToken = (token) => {
  if (token) {
    sessionStorage.setItem('gaurilabs_access_token', token);
  } else {
    sessionStorage.removeItem('gaurilabs_access_token');
    sessionStorage.removeItem('phantom_connected'); // clear wallet flag too
  }
};

axios.defaults.withCredentials = true;

axios.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState({ solana: null, evm: null });
  const [ssoError, setSsoError] = useState(null);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const loadWallets = (userData) => {
    if (userData?.wallets) {
      const solanaWallet = userData.wallets.find(w => w.chain === 'solana');
      // EVM hidden — only Solana matters for trading
      setWallets({ solana: solanaWallet || null, evm: null });
    }
  };

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
      loadWallets(data);
    } catch (error) {
      try {
        const { data: refreshData } = await axios.post(`${API_URL}/api/auth/refresh`, {});
        if (refreshData.access_token) {
          setStoredToken(refreshData.access_token);
          const { data } = await axios.get(`${API_URL}/api/auth/me`);
          setUser(data);
          loadWallets(data);
        } else {
          setStoredToken(null);
          setUser(false);
        }
      } catch {
        setStoredToken(null);
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ── SSO Handler ──────────────────────────────────────────────────────────
  const handleSSOToken = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('token');
    if (!ssoToken) return false;

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    try {
      try { await axios.post(`${API_URL}/api/auth/logout`, {}); } catch {}
      setStoredToken(null);

      const { data } = await axios.post(`${API_URL}/api/auth/sso`, { token: ssoToken });
      if (data.access_token) {
        setStoredToken(data.access_token);
        const meRes = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${data.access_token}` }
        });
        setUser(meRes.data);
        loadWallets(meRes.data);
        const role = meRes.data?.role;
        if (role === 'SUB_USER') {
          window.location.replace('/trade/volume-bot');
        } else {
          window.location.replace('/dashboard');
        }
        return true;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "SSO login failed. Please log in manually.";
      setSsoError(msg);
      setStoredToken(null);
      setUser(false);
      window.location.replace('/login');
    }
    return false;
  }, []);

  useEffect(() => {
    const init = async () => {
      const isSSORoute = window.location.pathname === '/sso';
      if (isSSORoute) {
        const handled = await handleSSOToken();
        if (handled) return;
        setLoading(false);
        return;
      }
      await checkAuth();
    };
    init();
  }, [checkAuth, handleSSOToken]);

  // ── Tab/browser close: fire disconnect-all using keepalive fetch ─────────
  useEffect(() => {
    const handleUnload = () => {
      const token = getStoredToken();
      if (!token) return;
      try {
        fetch(`${API_URL}/api/wallet/disconnect-all`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          keepalive: true,
        });
      } catch {}
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // ── Login ────────────────────────────────────────────────────────────────
  const login = async (email, password, captcha_token = '') => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
        ...(captcha_token ? { captcha_token } : {}),
      });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data);
      try {
        const meRes = await axios.get(`${API_URL}/api/auth/me`);
        if (meRes.data.wallets) loadWallets(meRes.data);
      } catch (e) { /* wallets load on next checkAuth */ }
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  const register = async (email, password, name, captcha_token = '') => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, {
        email, password, name,
        ...(captcha_token ? { captcha_token } : {}),
      });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data);
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  // ── Logout — clears session, wallet connections, and MongoDB records ─────
  const logout = async () => {
    try {
      // Clear wallet connections from MongoDB first
      await axios.post(`${API_URL}/api/wallet/disconnect-all`, {});
    } catch (error) {
      console.error('Wallet disconnect error:', error);
    }
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setStoredToken(null); // clears token + phantom_connected flag
      setUser(false);
      setWallets({ solana: null, evm: null });
    }
  };

  const connectWallet = async (chain, address, walletType) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/wallet/connect`, {
        chain, address, wallet_type: walletType
      });
      setWallets(prev => ({ ...prev, [chain]: data.wallet }));
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const disconnectWallet = async (chain) => {
    try {
      await axios.delete(`${API_URL}/api/wallet/disconnect/${chain}`);
      setWallets(prev => ({ ...prev, [chain]: null }));
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const isAdmin = user && (user.role === 'admin' || user.role === 'SUPER_ADMIN');
  const isSubUser = user && user.role === 'SUB_USER';

  const value = {
    user, loading, wallets, isAdmin, isSubUser, ssoError,
    login, register, logout, checkAuth, connectWallet, disconnectWallet,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
